import { forwardRef, ReactElement, Ref, useMemo, useState } from 'react';
import { Box, Dialog, Slide, Typography } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { CustomerFiles } from '@prisma/client';
import useResolvedFileUrls from '../../../../hooks/global/useResolvedFileUrls';
import MyImageGallery from '../../../global/ImageGallery';
import FileCard from './FileCard';
import ShowAllLink from './ShowAllLink';

interface Props {
  files: CustomerFiles[];
  timezone: string;
  onUploadFile: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreError?: boolean;
  onLoadMore?: () => void;
}

const Transition = forwardRef(
  (
    props: TransitionProps & {
      children: ReactElement;
    },
    ref: Ref<unknown>,
    // eslint-disable-next-line react/jsx-props-no-spreading
  ) => <Slide direction="up" ref={ref} {...props} />,
);

function FilesList({
  files,
  timezone,
  onUploadFile,
  hasMore = false,
  isLoadingMore = false,
  loadMoreError = false,
  onLoadMore,
}: Props) {
  const { getDisplayUrl } = useResolvedFileUrls(files);
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  const [failedImageIds, setFailedImageIds] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const markImageFailed = (fileId: string) =>
    setFailedImageIds(prev => new Set(prev).add(fileId));

  const imageFiles = useMemo(
    () =>
      files.filter(
        file => file.type.startsWith('image/') && !failedImageIds.has(file.id),
      ),
    [files, failedImageIds],
  );
  const galleryImageUrls = useMemo(
    () => imageFiles.map(file => getDisplayUrl(file.url)),
    [imageFiles, getDisplayUrl],
  );

  const openImageGallery = (file: CustomerFiles) => {
    const imageIndex = imageFiles.findIndex(image => image.id === file.id);
    if (imageIndex === -1) return;
    setGalleryIndex(imageIndex);
  };

  return (
    <Box sx={{ marginTop: '40px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Files
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={onUploadFile}
          sx={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'primary.main',
            cursor: 'pointer',
            typography: 'h5',
            fontWeight: 700,
          }}>
          Upload file
        </Box>
      </Box>
      {files.length === 0 ? (
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          No files yet.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px',
          }}>
          {files.map(file => (
            <FileCard
              key={file.id}
              file={file}
              timezone={timezone}
              displayUrl={getDisplayUrl(file.url)}
              onImageError={() => markImageFailed(file.id)}
              onOpenImage={() => openImageGallery(file)}
            />
          ))}
        </Box>
      )}
      {loadMoreError && (
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', marginTop: '28px' }}>
          There was an error loading more files.
        </Typography>
      )}
      {hasMore && onLoadMore && (
        <ShowAllLink
          label={loadMoreError ? 'Retry' : 'Show more'}
          isLoading={isLoadingMore}
          onClick={onLoadMore}
        />
      )}
      <Dialog
        fullScreen
        open={galleryIndex !== null}
        onClose={() => setGalleryIndex(null)}
        TransitionComponent={Transition}
        className="gallery-modal">
        <MyImageGallery
          images={galleryImageUrls}
          closeGallery={() => setGalleryIndex(null)}
          currentIndex={galleryIndex ?? 0}
        />
      </Dialog>
    </Box>
  );
}

export default FilesList;
